import { useState } from 'react';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { useIsAuthenticated } from 'root/src/client/v2/auth/hooks/useIsAuthenticated';
import { useIsAdmin } from 'root/src/client/v2/auth/hooks/useIsAdmin';
import { useMaxDaresByPlatforms } from 'root/src/client/logic/dropEvent/hooks/useMaxDaresByPlatforms';
import { usePointsEligibility } from 'root/src/client/v2/drop/hooks/usePointsEligibility';
import { isDropComingSoon } from 'root/src/shared/drops/helpers/isDropComingSoon.helper';
import { RedesignReclaimKeySelectionModal } from 'root/src/client/v2/drop/features/KeySelectionModal/components/RedesignReclaimKeySelectionModal';
import { ParticipationDialog } from 'root/src/client/v2/_pages/drop/redesign/ParticipationDialog';
import { PointsApplicationDialog } from 'root/src/client/v2/_pages/drop/redesign/PointsApplicationDialog';
import { ReservationDialog } from 'root/src/client/v2/_pages/drop/redesign/ReservationDialog';
import { RedesignHowToEarnPointsModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignHowToEarnPointsModal';
import { RedesignSponsorshipDetailsModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignSponsorshipDetailsModal';
import { DropNotifyButton } from 'root/src/client/v2/_pages/drop/redesign/DropNotifyButton';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { ProgressBar, toPercentage } from 'root/src/client/v2/common/components/ui/progress';
import { Popover, PopoverTrigger, PopoverContent } from 'root/src/client/v2/common/components/ui/popover';
import { Img } from 'root/src/client/v2/common/components/ui/image';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { buildAssetUrl } from 'root/src/client/v2/common/helpers/assetUrl';
import { upscaleBoxArt } from 'root/src/client/v2/_pages/drops/redesign/dropImage';
import { formatNumberWithCommas } from 'root/src/shared/util/formatNumberWithCommas';
import { getRewardTypeName } from 'root/src/shared/rewards/helpers/rewardType.helpers';
const PointsBar = ({ drop }) => {
    var _a, _b;
    // Only creators who can actually earn points (partner + CCV, see usePointsEligibility) plus
    // admins see the meter. Not tied to viewership breakpoints or the initial key pool.
    const { showPointsUi } = usePointsEligibility(drop);
    if (!showPointsUi || !drop.awardPoints || !drop.pointsLimit) {
        return null;
    }
    const remaining = Math.max(0, drop.pointsLimit - ((_a = drop.pointsAwarded) !== null && _a !== void 0 ? _a : 0) - ((_b = drop.pointsReserved) !== null && _b !== void 0 ? _b : 0));
    return (<div>
			<div className="flex items-center justify-between text-xs">
				<span className="text-muted-foreground">Points remaining</span>
				<span className="font-semibold text-foreground">{formatNumberWithCommas(remaining)} / {formatNumberWithCommas(drop.pointsLimit)}</span>
			</div>
			<ProgressBar value={toPercentage(remaining, drop.pointsLimit)} className="mt-2"/>
			{drop.estimatedPointsToEarn ? (<p className="mt-2 text-[11px] font-medium text-neon-cyan">Earn up to {formatNumberWithCommas(drop.estimatedPointsToEarn)} points</p>) : null}
		</div>);
};
// The headline count for a drop is the initial reward (key/code) a creator gets for joining,
// not the dares' prize pool. Mirrors the legacy hero "X Keys remaining out of Y total" bar
// (web/record/DropEvent/Progress.tsx), reading `initialProduct[0]` available/used.
const InitialProductBar = ({ drop }) => {
    var _a, _b;
    const initialProductData = (_a = drop.initialProduct) === null || _a === void 0 ? void 0 : _a[0];
    if (!initialProductData) {
        return null;
    }
    const available = Math.max(0, initialProductData.available);
    // Mirror legacy Progress.tsx: keep the bar visible even when the pool is depleted or has no
    // counter yet (show "0 / N" or "0 / 0"). Clamp total; toPercentage guards against total === 0.
    const total = Math.max(0, initialProductData.available + ((_b = initialProductData.used) !== null && _b !== void 0 ? _b : 0));
    const rewardName = getRewardTypeName(initialProductData.type, { amount: total, capitalize: true });
    return (<div>
			<div className="flex items-center justify-between text-xs">
				<span className="text-muted-foreground">{rewardName} remaining</span>
				<span className="font-semibold text-foreground">{available} / {total}</span>
			</div>
			<ProgressBar value={toPercentage(available, total)} className="mt-2"/>
		</div>);
};
// A compact preview of the drop's initial reward (the key/code/bundle a creator can claim by
// joining) shown at the top of the card. Neutral "Claim your reward" framing, since the visitor
// may not have participated yet. Complements InitialProductBar (which shows the remaining count).
const InitialRewardCard = ({ drop }) => {
    var _a, _b, _c, _d, _e, _f;
    const [infoOpen, setInfoOpen] = useState(false);
    const initialProductData = (_a = drop.initialProduct) === null || _a === void 0 ? void 0 : _a[0];
    if (!initialProductData) {
        return null;
    }
    const hasDares = ((_c = (_b = drop.dares) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0) > 0;
    const onJumpToDares = () => {
        var _a;
        setInfoOpen(false);
        (_a = document.getElementById('dares')) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: 'smooth' });
    };
    const rewardTypeName = getRewardTypeName(initialProductData.type, { amount: initialProductData.amount, capitalize: true });
    // Box art + label come from the drop's game (same source RedesignInitialProductDisplayModal uses).
    const game = (_f = (_e = (_d = drop.dares) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.games) === null || _f === void 0 ? void 0 : _f[0];
    const rewardName = (game === null || game === void 0 ? void 0 : game.label) || initialProductData.name || rewardTypeName;
    const metaParts = [rewardTypeName, initialProductData.platformReadable, initialProductData.region].filter(Boolean);
    const imageSrc = buildAssetUrl(upscaleBoxArt(game === null || game === void 0 ? void 0 : game.image, '600x800'), { w: 160, q: 80, fmt: 'webp' });
    return (<div className="mb-5 rounded-xl border border-solid border-white/[0.06] bg-white/[0.02] p-3">
			<div className="flex items-center justify-between gap-2">
				<p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Claim your reward</p>
				<Popover open={infoOpen} onOpenChange={setInfoOpen}>
					<PopoverTrigger asChild>
						<button type="button" aria-label="How to claim this reward" className="-my-1 flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground/70 transition-colors hover:bg-white/[0.06] hover:text-foreground">
							<InfoOutlinedIcon sx={{ fontSize: 14 }}/>
						</button>
					</PopoverTrigger>
					<PopoverContent align="end" className="w-64">
						<p className="text-xs font-semibold text-foreground">How to claim</p>
						<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
							You get this reward when you accept a dare from this drop.
						</p>
						{hasDares && (<Button variant="secondary" onClick={onJumpToDares} className="mt-3 w-full rounded-lg text-xs font-semibold">
								<KeyboardArrowDownIcon sx={{ fontSize: 16 }}/>
								View dares
							</Button>)}
					</PopoverContent>
				</Popover>
			</div>
			<div className="mt-2 flex items-center gap-3">
				{imageSrc ? (<Img src={imageSrc} alt={rewardName} className="h-16 w-12 shrink-0 rounded-md border border-solid border-white/10 object-cover"/>) : (<span className="flex h-16 w-12 shrink-0 items-center justify-center rounded-md border border-solid border-white/10 bg-white/[0.03] text-neon-green">
						<CardGiftcardIcon sx={{ fontSize: 22 }}/>
					</span>)}
				<div className="min-w-0">
					<p className="truncate text-sm font-semibold text-foreground">{rewardName}</p>
					{metaParts.length > 0 && <p className="mt-0.5 truncate text-xs text-muted-foreground">{metaParts.join(' · ')}</p>}
				</div>
			</div>
		</div>);
};
const NotifyButton = ({ drop }) => {
    const isAuthenticated = useIsAuthenticated();
    if (!isAuthenticated || !isDropComingSoon(drop)) {
        return null;
    }
    return (<DropNotifyButton dropId={drop.id} variant="secondary" label="Notify me when live" className="mt-5 w-full rounded-lg text-xs font-semibold"/>);
};
const ReClaimKeyButton = ({ drop }) => {
    const redesignModal = useRedesignModal();
    const isAuthenticated = useIsAuthenticated();
    const initialReclaimable = Boolean(drop.canUserReclaimInitialReward);
    const [canReclaim, setCanReclaim] = useState(initialReclaimable);
    if (!isAuthenticated || !canReclaim) {
        return null;
    }
    const onReclaim = () => redesignModal.show(<RedesignReclaimKeySelectionModal drop={{ id: drop.id, isGameFree: drop.isGameFree, dares: drop.dares, initialProduct: drop.initialProduct }} onSuccess={() => setCanReclaim(false)}/>);
    return (<Button variant="outline" onClick={onReclaim} className="mt-3 w-full rounded-lg text-xs font-semibold">
			Re-claim your key
		</Button>);
};
const SponsorshipDetailsButton = ({ drop }) => {
    const redesignModal = useRedesignModal();
    const isAuthenticated = useIsAuthenticated();
    const { isAdmin } = useIsAdmin();
    // Dare-eligible creators (viewership clears a breakpoint), with an admin bypass so internal
    // accounts can always inspect the sponsorship terms.
    const isEligible = useMaxDaresByPlatforms({ fallbackDrop: drop }) > 0;
    if (!isAuthenticated || (!isEligible && !isAdmin)) {
        return null;
    }
    return (<Button variant="outline" onClick={() => redesignModal.show(<RedesignSponsorshipDetailsModal drop={drop}/>)} className="mt-3 w-full rounded-lg text-xs font-semibold">
			Sponsorship details
		</Button>);
};
const HowToEarnPointsButton = ({ drop }) => {
    const redesignModal = useRedesignModal();
    const { showPointsUi } = usePointsEligibility(drop);
    if (!showPointsUi || !drop.awardPoints) {
        return null;
    }
    return (<button type="button" onClick={() => redesignModal.show(<RedesignHowToEarnPointsModal drop={drop}/>)} className="mt-3 text-[11px] font-medium text-neon-cyan transition-colors hover:text-neon-cyan/80">
			How to earn points
		</button>);
};
// The drop's primary info + action panel. Lives in the hero (right column) so it fills the
// space beside the title above the fold; the Overview section below renders full-width.
export const DropDetailsCard = ({ drop, className }) => {
    var _a;
    return (<div className={cn('rounded-2xl border border-solid border-white/10 bg-card/80 p-5 shadow-2xl shadow-black/50 backdrop-blur-xl', className)} data-onboarding="drop-details">
		<InitialRewardCard drop={drop}/>
		<h3 className="font-display text-sm font-semibold text-foreground">Drop Details</h3>
		<dl className="mt-4 space-y-3 text-sm">
			<div className="flex items-center justify-between gap-3">
				<dt className="text-muted-foreground">Publisher</dt>
				<dd className="truncate font-medium text-foreground">{(_a = drop.brand) === null || _a === void 0 ? void 0 : _a.brandName}</dd>
			</div>
			{drop.isGameFree && (<div className="flex items-center justify-between gap-3">
					<dt className="text-muted-foreground">Game</dt>
					<dd className="font-medium text-neon-green">Free to play</dd>
				</div>)}
		</dl>
		<div className="mt-5 space-y-5">
			<InitialProductBar drop={drop}/>
			<PointsBar drop={drop}/>
		</div>
		<HowToEarnPointsButton drop={drop}/>
		<PointsApplicationDialog drop={drop}/>
		<NotifyButton drop={drop}/>
		<ReservationDialog drop={drop}/>
		<ReClaimKeyButton drop={drop}/>
		<SponsorshipDetailsButton drop={drop}/>
		<ParticipationDialog drop={drop}/>
	</div>);
};
